import { Dialog } from "quasar";

interface VersionInfo {
  build_version: string;
  schema_hash: string;
}

export function initVersionCheck() {
  try {
    installChunkLoadingErrorHandler();
    console.log("Installed chunk loading error handler");
  } catch (error) {
    console.error("Failed to install chunk loading error handler:", error);
  }

  try {
    if (import.meta.env.VITE_VANTOOL_SCHEMA_HASH) {
      startVersionPolling(1000 * 60 * 1);
    } else {
      console.warn(
        "VANTOOL_SCHEMA_HASH is not set. Version checking disabled.",
      );
    }
  } catch (error) {
    console.error("Failed to initialize version polling:", error);
  }
}

const CHUNK_LOAD_ERROR_PATTERN =
  /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Unable to preload CSS/i;
const RELOAD_TIMESTAMP_KEY = "vantool:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 60 * 1000;

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : (error as { message?: unknown } | null | undefined)?.message;
  return typeof message === "string" && CHUNK_LOAD_ERROR_PATTERN.test(message);
}

let chunkReloadDialogOpen = false;
const chunkReloadDeclines: Array<() => void> = [];

function isChunkReloadCoolingDown(): boolean {
  try {
    const lastDecline = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY));
    return (
      Boolean(lastDecline) && Date.now() - lastDecline < RELOAD_COOLDOWN_MS
    );
  } catch {
    return false;
  }
}

function stampChunkReloadCooldown() {
  try {
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    return;
  }
}

export function promptReloadForNewVersion(onDecline?: () => void): boolean {
  if (isChunkReloadCoolingDown()) {
    return false;
  }
  if (onDecline) {
    chunkReloadDeclines.push(onDecline);
  }
  if (chunkReloadDialogOpen) {
    return true;
  }

  chunkReloadDialogOpen = true;
  let settled = false;
  const decline = () => {
    if (settled) {
      return;
    }
    settled = true;
    chunkReloadDialogOpen = false;
    stampChunkReloadCooldown();
    const callbacks = chunkReloadDeclines.splice(0);
    for (const callback of callbacks) {
      callback();
    }
  };

  try {
    Dialog.create({
      title: "Important Update Required",
      message: "Please reload the page to apply the latest changes.",
      class: "bg-primary text-black",
      persistent: true,
      ok: {
        label: "Reload Now",
        color: "black",
        textColor: "primary",
      },
      cancel: {
        label: "Later",
        textColor: "black",
        flat: true,
      },
    })
      .onOk(() => {
        if (settled) {
          return;
        }
        settled = true;
        chunkReloadDialogOpen = false;
        chunkReloadDeclines.splice(0);
        window.location.reload();
      })
      .onCancel(decline)
      .onDismiss(decline);
  } catch {
    chunkReloadDialogOpen = false;
    chunkReloadDeclines.splice(0);
    return false;
  }

  return true;
}

function installChunkLoadingErrorHandler() {
  window.addEventListener("vite:preloadError", (event) => {
    if (promptReloadForNewVersion()) {
      event.preventDefault();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (!isChunkLoadError(event.reason)) {
      return;
    }
    if (promptReloadForNewVersion()) {
      event.preventDefault();
    }
  });
}

function startVersionPolling(interval: number) {
  let isShowingNewVersionDialog = false;
  const currentSchemaHash = import.meta.env.VITE_VANTOOL_SCHEMA_HASH;

  if (!currentSchemaHash) {
    console.warn(
      "VITE_VANTOOL_SCHEMA_HASH is not set. Version checking disabled.",
    );
    return;
  }

  function onReloadDeclined() {
    console.warn("User declined reload. Not reloading.");
    isShowingNewVersionDialog = false;
    clearInterval(intervalId);
    startVersionPolling(1000 * 60 * 10); // reduce interval to 10 minutes
  }

  const intervalId = setInterval(() => {
    fetch("/api/internal/vantool/version")
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json() as Promise<VersionInfo>;
      })
      .then((version) => {
        if (version.schema_hash === currentSchemaHash) {
          return;
        }

        if (isShowingNewVersionDialog) {
          return;
        }

        console.log(
          `Vantool API changed! Current: ${currentSchemaHash}, New: ${version.schema_hash}.`,
        );

        isShowingNewVersionDialog = true;
        const message = "Please reload the page to apply the latest changes.";
        try {
          Dialog.create({
            title: "Important Update Required",
            message,
            class: "bg-primary text-black",
            persistent: true,
            ok: {
              label: "Reload Now",
              color: "black",
              textColor: "primary",
            },
            cancel: {
              label: "Later",
              textColor: "black",
              flat: true,
            },
          })
            .onOk(() => window.location.reload())
            .onCancel(() => onReloadDeclined());
        } catch (error) {
          if (confirm(message)) {
            window.location.reload();
          } else {
            onReloadDeclined();
          }
        }
      })
      .catch((error) => {
        console.error("Failed to poll version:", error);
      });
  }, interval);

  return intervalId;
}
